import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { SnCamera, SnLayer, SnLocation, SnTag } from '@lib/api/entities';
import { SnCurrentUser } from '@lib/auth/services/current-user.service';
import { CameraSearchDialogComponent } from '@lib/cameras/components/camera-search-dialog';
import { SnFormFieldConfig } from '@lib/form';
import { LocationSearchDialogComponent } from '@lib/locations/components/location-search-dialog/location-search-dialog.component';
import { t } from '@lib/translation';
import { FsPermission } from 'libs/api/entities/src/lib/permission';
import { TagLocationDialogComponent } from 'libs/tags/src/lib/components';

import { EditPolicyGroupDialogComponent } from '..';
import { FsPolicy } from '../../models';
import { LayersSearchDialogComponent } from '../layers-search-dialog/layers-search-dialog.component';
import { PermissionsSearchDialogComponent } from '../permissions-search-dialog/permissions-search-dialog.component';

@Component({
  selector: 'fs-add-policy-dialog',
  templateUrl: './add-policy-dialog.component.html',
  styleUrls: ['./add-policy-dialog.component.scss'],
})
export class AddPolicyDialogComponent implements OnInit, OnDestroy {
  public formModel = new FsPolicy({});
  public form = new UntypedFormGroup({});
  public resourceType: string;

  public formFields: SnFormFieldConfig[] = [];

  public customPolicy: FsPermission[] = [];

  private readonly _destroy$ = new Subject();

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      policy?: FsPolicy;
    },
    private readonly _dialogRef: MatDialogRef<EditPolicyGroupDialogComponent>,
    private readonly _dialog: MatDialog,
    private readonly _cd: ChangeDetectorRef,
    private readonly _currentUser: SnCurrentUser,
  ) {}

  get isSupportUser(): boolean {
    return this._currentUser.isSupportUser;
  }

  public getAllSelectedText(): string {
    const type = this.formModel.type || 'camera';
    
    switch (type) {
      case 'camera':
        return t('users.policyGroups.allCamerasSelected');
      case 'location':
        return t('users.policyGroups.allLocationsSelected');
      case 'layer':
        return t('users.policyGroups.allLayersSelected');
    }
  }


  public ngOnInit(): void {
    this.formModel = this.data.policy || this.formModel;
    this.formModel.selectAll = this.formModel.resources.includes('*');
    this.resourceType = this.formModel.type;
    this._initForm();
    this._initTagForm();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public setCameraResource(initForm?: boolean): void {
    this.formModel.permissions = ['cameras.view_camera'];
    if (!this.formModel.resources[0]?.id_In?.length) {
      this.formModel.resources = [
        {
          type: 'cameras.Camera',
          id_In: [],
        },
      ];
    }
    if (initForm) {
      this._initForm();
    }
    this._cd.detectChanges();
  }

  public setLayerResource(initForm?: boolean): void {
    this.formModel.permissions = ['maps.view_layer'];
    if (!this.formModel.resources[0]?.id__in?.length) {
      this.formModel.resources = [
        {
          type: 'maps.Layer',
          id__in: [],
        },
      ];
    }
    if (initForm) {
      this._initForm();
    }
    this._cd.detectChanges();
  }

  public setLocationResource(initForm?: boolean): void {
    this.formModel.permissions = ['cameras.view_camera', 'maps.view_location'];

    if (!this.formModel.resources[0]?.location_In?.length) {
      this.formModel.resources = [
        {
          type: 'cameras.Camera',
          location_In: [],
        },
      ];
    }
    if (initForm) {
      this._initForm();
    }
    this._cd.detectChanges();
  }

  public setCameraTagsResource(initForm?: boolean): void {
    this.formModel.permissions = ['cameras.view_camera'];
    if (!this.formModel.resources[0]?.tagRecords_Tag_Id_In?.length) {
      this.formModel.resources = [
        {
          type: 'cameras.Camera',
          tagRecords_Tag_Id_In: [],
        },
      ];
    }
    if (initForm) {
      this._initTagForm();
    }
    this._cd.detectChanges();
  }

  public setLocationTagResource(initForm?: boolean): void {
    this.formModel.permissions = ['cameras.view_camera', 'maps.view_location'];
    if (!this.formModel.resources[0]?.location_TagRecords_Tag_Id_In?.length) {
      this.formModel.resources = [
        {
          type: 'cameras.Camera',
          location_TagRecords_Tag_Id_In: [],
        },
      ];
    }
    if (initForm) {
      this._initTagForm();
    }
    this._cd.detectChanges();
  }

  public setCustomResource(initForm?: boolean): void {
    this.formModel.permissions = [];
    this.formModel.resources = ['*'];
    if (initForm) {
      this._initForm();
    }
    this._cd.detectChanges();
  }

  public addCameraTags(): void {
    const dialogRef = this._dialog.open(TagLocationDialogComponent, {
      data: {
        multiple: true,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter((result) => !!result),
        takeUntil(this._destroy$),
      )
      .subscribe((tags) => {
        if (!this.formModel.selectAll) {
          this.setCameraTagsResource();
        }
        const newTag = tags.filter((tag) => {
          return !this.formModel.resources[0]?.tagRecords_Tag_Id_In?.includes(tag.id);
        });

        const existing = this.formModel.resources[0];
        existing.tagRecords_Tag_Id_In?.push(...newTag.map((t) => t.id));
        this.data.policy.tags.push(...newTag);
        this._cd.detectChanges();
      });
  }

  public addLocationTags(): void {
    const dialogRef = this._dialog.open(TagLocationDialogComponent, {
      data: {
        multiple: true,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter((result) => !!result),
        takeUntil(this._destroy$),
      )
      .subscribe((tags) => {
        if (!this.formModel.selectAll) {
          this.setLocationTagResource();
        }
        const newTag = tags.filter((tag) => {
          return !this.formModel.resources[0]?.location_TagRecords_Tag_Id_In?.includes(tag.id);
        });

        const existing = this.formModel.resources[0];
        existing.location_TagRecords_Tag_Id_In?.push(...newTag.map((t) => t.id));
        this.data.policy.tags.push(...newTag);
        this._cd.detectChanges();
      });
  }

  public addCameras(): void {
    const dialogRef = this._dialog.open(CameraSearchDialogComponent, {
      data: {
        multiple: true,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter((result) => !!result),
        takeUntil(this._destroy$),
      )
      .subscribe((cameras) => {
        if (!this.formModel.selectAll) {
          this.setCameraResource();
        }
        const newCams = cameras.filter((cam) => {
          return !this.formModel.resources[0]?.id_In?.includes(cam.id);
        });
        const existing = this.formModel.resources[0];
        existing.id_In?.push(...newCams.map((c) => c.id));
        this.data.policy.cameras.push(...newCams);
        this._cd.detectChanges();
      });
  }

  public addLocations(): void {
    const dialogRef = this._dialog.open(LocationSearchDialogComponent, {
      minWidth: '40vw',
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter((result) => !!result),
        takeUntil(this._destroy$),
      )
      .subscribe((locations) => {
        if (!this.formModel.selectAll) {
          this.setLocationResource();
        }
        const newLocs = locations.filter((loc) => {
          return !this.formModel.resources[0]?.location_In?.includes(loc.id);
        });

        const existing = this.formModel.resources[0];
        existing.location_In?.push(...newLocs.map((l) => l.id));
        this.data.policy.locations.push(...newLocs);
        this._cd.detectChanges();
      });
  }

  public addLayers(): void {
    const dialogRef = this._dialog.open(LayersSearchDialogComponent, {
      minWidth: '40vw',
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter((result) => !!result),
        takeUntil(this._destroy$),
      )
      .subscribe((layers) => {
        if (!this.formModel.selectAll) {
          this.setLayerResource();
        }
        const newLayers = layers.filter((layer) => {
          return !this.formModel.resources[0]?.id__in?.includes(layer.id);
        });

        const existing = this.formModel.resources[0];
        existing.id__in?.push(...newLayers.map((layer) => layer.id));
        this.data.policy.layers.push(...newLayers);
        this._cd.detectChanges();
      });
  }

  public addPermissions(): void {
    const dialogRef = this._dialog.open(PermissionsSearchDialogComponent, {
      width: '80vw',
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter((result) => !!result),
        takeUntil(this._destroy$),
      )
      .subscribe((permissions) => {
        const newPermissions = permissions.filter((permission) => {
          return !this.data.policy.permissions.includes(
            `${permission.appLabel}.${permission.codename}`,
          );
        });

        this.customPolicy.push(...newPermissions);

        const formattedPermissions = this.customPolicy.map((permission) => {
          return `${permission.appLabel}.${permission.codename}`;
        });

        this.data.policy.permissions = formattedPermissions;
        this.data.policy.isSystemManaged = false;
        this.data.policy.isOrganizationManaged = true;
        this._cd.detectChanges();
      });
  }

  public removeCamera(camera: SnCamera): void {
    const resource = this.data.policy.resources[0];
    resource.id_In.splice(resource.id_In.indexOf(camera.id), 1);
    this.data.policy.cameras = this.data.policy.cameras.filter((l) => l.id !== camera.id);
  }

  public removeLocationTag(tag: SnTag): void {
    const resource = this.data.policy.resources[0];
    resource.location_TagRecords_Tag_Id_In.splice(
      resource.location_TagRecords_Tag_Id_In.indexOf(tag.id),
      1,
    );
    this.data.policy.tags = this.data.policy.tags.filter((t) => t.id !== tag.id);
  }

  public removeCameraTag(tag: SnTag): void {
    const resource = this.data.policy.resources[0];
    resource.tagRecords_Tag_Id_In.splice(resource.tagRecords_Tag_Id_In.indexOf(tag.id), 1);
    this.data.policy.tags = this.data.policy.tags.filter((t) => t.id !== tag.id);
  }

  public removeLocation(location: SnLocation): void {
    const resource = this.data.policy.resources[0];
    resource.location_In.splice(resource.location_In.indexOf(location.id), 1);
    this.data.policy['locations'] = this.data.policy['locations'].filter(
      (l) => l.id !== location.id,
    );
  }

  public removeLayer(layer: SnLayer): void {
    this.data.policy.layers = this.data.policy.layers.filter((l) => l.id !== layer.id);
  }

  public removePermission(permission: FsPermission): void {
    this.customPolicy = this.customPolicy.filter((p) => p.codename !== permission.codename);
    const formattedPermissions = this.customPolicy.map((perm) => {
      return `${perm.appLabel}.${perm.codename}`;
    });

    this.data.policy.permissions = formattedPermissions;
    this._cd.detectChanges();
  }

  public submit(): void {
    if (this.data.policy.resources[0].location_In) {
      this.data.policy.resources[1] = {
        type: 'maps.Location',
        id_In: this.data.policy.resources[0].location_In,
      };
    } else if (this.data.policy.resources[0].tagRecords_Tag_Id_In) {
      this.data.policy.resources[1] = {
        type: 'cameras.Camera',
        tagRecords_Tag_Id_In: this.data.policy.resources[0].tagRecords_Tag_Id_In,
      };
    } else if (this.data.policy.resources[0].location_TagRecords_Tag_Id_In) {
      this.data.policy.resources[1] = {
        type: 'cameras.Camera',
        location_TagRecords_Tag_Id_In: this.data.policy.resources[0].location_TagRecords_Tag_Id_In,
      };
    }
    if (this.formModel.selectAll) {
      this.formModel.resources = ['*'];
      this.formModel.cameras = [];
      this.formModel.locations = [];
      this.formModel.tags = [];
      this.data.policy.resources = this.formModel.resources;
    }
    this._dialogRef.close({
      policy: this.formModel,
    });
  }

  private _initForm(): void {
    this.formFields = [
      {
        key: 'name',
        type: 'input',
        templateOptions: {
          label: t('common.name'),
          type: 'text',
          required: true,
        },
      },
      {
        fieldGroupClassName: 'add-policy-row',
        fieldGroup: [
          {
            key: 'isDenyPolicy',
            type: 'drag-slide-toggle',
            className: 'deny-access-col',
            templateOptions: {
              toggleLabel: t('common.denyAccess'),
            },
          },
          {
            key: 'selectAll',
            type: 'drag-slide-toggle',
            className: 'select-all-col',
            templateOptions: {
              toggleLabel: this.getAllSelectedText(),
            },
            hideExpression: () => this.formModel.type === 'permission',
          },
        ],
      },
    ];
  }

  private _initTagForm(): void {
    this.formFields = [
      {
        key: 'name',
        type: 'input',
        templateOptions: {
          label: t('common.name'),
          type: 'text',
          required: true,
        },
      },
      {
        fieldGroupClassName: 'tag-row',
        fieldGroup: [
          {
            key: 'isDenyPolicy',
            type: 'drag-slide-toggle',
            className: 'deny-access-col',
            templateOptions: {
              toggleLabel: t('common.denyAccess'),
            },
          },
        ],
      },
    ];
  }
}
